import React, { useState, useEffect } from "react";
import {
  PhoneIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  fetchPhoneroLiveCalls,
  getPhoneroStoredCalls,
  fetchPhoneroStats,
  refreshPhoneroStats,
} from "../../../api/company";
import RoleOnly from "../../../routes/roleOnly";
import { ItemStatus } from "../../Utils";
import { Link } from "react-router-dom";
import { PMDialog } from "../../utils/PMDialog";
import { NotesAdvancedSection } from "../notes/notesAdvancedSection";
import CreateCaseDialog from "./CreateCaseDialog";
import AddManualCall from "./AddManualCall";
import { toast } from "react-hot-toast";
import { processCallWithoutCase } from "../../../api/case";

// Reusable CallItem component
const CallItem = ({
  call,
  index,
  showHover = true,
  onOpenNotes,
  onOpenCreateCase,
  onProcessWithoutCase,
}) => {
  const { t } = useTranslation();

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds < 0) return "0s";

    // Handle extremely large numbers (likely errors)
    if (seconds > 86400) {
      // More than 24 hours
      console.warn("Suspicious duration value:", seconds);
      return "0s";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return minutes > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${remainingSeconds}s`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("nb-NO");
  };

  const getCallStatusIcon = (status) => {
    switch (status) {
      case "answered":
        return <PhoneIcon className="h-4 w-4 text-green-600" />;
      case "missed":
        return <PhoneIcon className="h-4 w-4 text-red-600" />;
      case "ended":
        return <PhoneIcon className="h-4 w-4 text-gray-600" />;
      case "processed":
        return <CheckCircleIcon className="h-4 w-4 text-blue-600" />;
      default:
        return <PhoneIcon className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getCaseStatusBadge = (caseStatus) => {
    if (!caseStatus || !caseStatus.has_case) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          No Case
        </span>
      );
    }

    const statusColors = {
      "NEW": "bg-blue-100 text-blue-800",
      "PROCESSING": "bg-yellow-100 text-yellow-800",
      "FINISHED": "bg-green-100 text-green-800",
      "CLOSED": "bg-gray-100 text-gray-800",
    };

    return (
      <div className="flex items-center space-x-2">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[caseStatus.case_status] || "bg-gray-100 text-gray-800"
          }`}
        >
          {caseStatus.case_status}
        </span>
        <Link
          to={caseStatus.case_url}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {caseStatus.case_number}
        </Link>
      </div>
    );
  };

  return (
    <li className={`${showHover ? "px-4 py-4 hover:bg-gray-50" : "py-3"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {getCallStatusIcon(call.status)}
          <div className={showHover ? "ml-4" : "ml-3"}>
            <p className="text-sm font-medium text-gray-900">
              {t("number.from")}: {call.from_number || "Unknown"}{" "}
              {call?.communication?.customer?.id
                ? `(${call?.communication?.customer?.displayTitle})`
                : ""}
              {call.manual_call && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 ml-2">
                  Manual
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500">
              {t("number.to")}: {call.to_number || "Unknown"}
            </p>
            <p className="text-xs text-gray-400">
              {formatDateTime(call.started_at)}
            </p>
            {call.agent && (
              <p className="text-xs text-blue-600 mt-1">
                {t("Agent")}: <Link to="">{call?.agent_details?.name}</Link> (
                {call.agent})
              </p>
            )}
            {call.queue_name && (
              <p className="text-xs text-gray-500 mt-1">
                {t("Queue")}: {call.queue_name}
              </p>
            )}
            {call.manual_call && call.manual_note && (
              <p className="text-xs text-gray-600 mt-1 italic">
                Note: {call.manual_note}
              </p>
            )}

            {/* Case Status */}
            <div className="mt-2">{getCaseStatusBadge(call.case_status)}</div>

            {call.communication ? (
              <p className="text-xs text-purple-600 mt-1">
                <Link
                  to={`/dashboard/customers/${call.communication.customer?.id}/communications/${call.communication.id}`}
                  className="hover:text-purple-800 underline"
                >
                  📞 View Communication (
                  {call.communication.customer?.displayTitle})
                </Link>
              </p>
            ) : (
              <div className="text-xs text-gray-500 mt-1">
                <p>{t("No communication found")}</p>
                {call.possible_customers ? (
                  call.possible_customers.length > 0 ? (
                    <div className="mt-1">
                      <p className="text-xs text-blue-600 font-medium">
                        Possible customers:
                      </p>
                      {call.possible_customers.map((customer, idx) => (
                        <div
                          key={customer.id}
                          className="text-xs text-gray-600 ml-2"
                        >
                          <Link
                            to={`/dashboard/customers/${customer.id}`}
                            className="hover:text-blue-600 underline"
                          >
                            {customer.name} (#{customer.customer_number})
                          </Link>
                          <span className="text-gray-400 ml-1">
                            - Matched on {customer.match_type} (
                            {customer.match_number})
                          </span>
                          {(customer.phone || customer.mobile) && (
                            <span className="text-gray-400 ml-1">
                              - Phone: {customer.phone || customer.mobile}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>{t("No possible customers found")}</p>
                  )
                ) : (
                  <p>{t("No possible customers found")}</p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              <ItemStatus item={call} />
            </p>
            <p className="text-sm text-gray-500">
              {formatDuration(call.duration)}
            </p>
          </div>
          {/* Action buttons */}
          <div className="flex items-center space-x-2">
            {/* Only show action buttons if call is not processed */}
            {!call.processed_at && (
              <>
                {/* Create Case button */}
                <button
                  onClick={() => onOpenCreateCase(call)}
                  className="inline-flex items-center p-2 text-sm font-medium text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                  title="Create or Link Case"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </button>

                {/* Process Without Case button */}
                <button
                  onClick={() => onProcessWithoutCase(call)}
                  className="inline-flex items-center p-2 text-sm font-medium text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors duration-200"
                  title="Process Without Case"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

// Search and Filter Component
const SearchAndFilter = ({ filters, onFiltersChange, onSearch }) => {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync localFilters when parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "answered", label: "Answered" },
    { value: "missed", label: "Missed" },
    { value: "ended", label: "Ended" },
    { value: "abandoned", label: "Abandoned" },
    { value: "processed", label: "Processed" },
  ];

  const periodOptions = [
    { value: "", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "this_week", label: "This Week" },
    { value: "this_month", label: "This Month" },
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);

    // Only update filters immediately for non-search fields
    // For search query, only update local state, don't trigger search
    if (key !== "query") {
      onFiltersChange(newFilters);
    }
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    setShowFilters(false);
    onSearch();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      query: "",
      status: "",
      agent: "",
      queue: "",
      period: "",
      start_date: "",
      end_date: "",
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onSearch();
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value && value !== ""
  );

  return (
    <div className="bg-white shadow rounded-lg p-4 mb-6">
      {/* Search Bar */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search calls by number, agent, queue..."
            value={localFilters.query || ""}
            onChange={(e) => handleFilterChange("query", e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                // Update filters with current search query and trigger search
                const newFilters = { ...localFilters, query: e.target.value };
                onFiltersChange(newFilters);
                onSearch();
              }
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => {
            // Update filters with current search query and trigger search
            const newFilters = { ...localFilters };
            onFiltersChange(newFilters);
            onSearch();
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Search
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 border rounded-md flex items-center space-x-2 ${
            hasActiveFilters
              ? "border-orange-300 bg-orange-50 text-orange-700"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          <FunnelIcon className="h-4 w-4" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="bg-orange-500 text-white text-xs rounded-full px-2 py-1">
              {Object.values(filters).filter((v) => v && v !== "").length}
            </span>
          )}
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="border-t border-gray-200 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={localFilters.status || ""}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Period Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period
              </label>
              <select
                value={localFilters.period || ""}
                onChange={(e) => handleFilterChange("period", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Agent Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent
              </label>
              <input
                type="text"
                placeholder="Search by agent name"
                value={localFilters.agent || ""}
                onChange={(e) => handleFilterChange("agent", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Queue Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Queue
              </label>
              <input
                type="text"
                placeholder="Search by queue name"
                value={localFilters.queue || ""}
                onChange={(e) => handleFilterChange("queue", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={localFilters.start_date || ""}
                onChange={(e) =>
                  handleFilterChange("start_date", e.target.value)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={localFilters.end_date || ""}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleClearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Clear all filters</span>
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) return null;

  const startItem = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      if (currentPage <= 3) {
        // Near start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near end
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{startItem}</span> to{" "}
            <span className="font-medium">{endItem}</span> of{" "}
            <span className="font-medium">{totalItems}</span> results
          </p>
        </div>
        <div>
          <nav
            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            aria-label="Pagination"
          >
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Previous</span>
              <ArrowPathIcon
                className="h-5 w-5 transform rotate-180"
                aria-hidden="true"
              />
            </button>

            {/* Page numbers */}
            {getPageNumbers().map((page, index) => (
              <button
                key={index}
                onClick={() =>
                  typeof page === "number" ? onPageChange(page) : null
                }
                disabled={page === "..."}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  page === "..."
                    ? "bg-white border-gray-300 text-gray-500 cursor-default"
                    : currentPage === page
                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Next</span>
              <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

const CallsList = ({ companyId }) => {
  const { t } = useTranslation();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    today: {
      totalCalls: 0,
      totalDuration: 0,
      answeredCalls: 0,
      missedCalls: 0,
      lastUpdated: null,
      error: null,
    },
    this_week: {
      totalCalls: 0,
      totalDuration: 0,
      answeredCalls: 0,
      missedCalls: 0,
      lastUpdated: null,
      error: null,
    },
    this_month: {
      totalCalls: 0,
      totalDuration: 0,
      answeredCalls: 0,
      missedCalls: 0,
      lastUpdated: null,
      error: null,
    },
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [refreshingStats, setRefreshingStats] = useState({});
  const [liveCalls, setLiveCalls] = useState([]);
  const [showLiveCalls, setShowLiveCalls] = useState(true);
  const [liveCallsLoading, setLiveCallsLoading] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showCreateCaseDialog, setShowCreateCaseDialog] = useState(false);
  const [selectedCallForCase, setSelectedCallForCase] = useState(null);
  const [showAddManualCall, setShowAddManualCall] = useState(false);

  // Search and filter state
  const [filters, setFilters] = useState({
    query: "",
    status: "",
    agent: "",
    queue: "",
    period: "this_month",
    start_date: "",
    end_date: "",
  });
  const [totalCalls, setTotalCalls] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [callsPerPage] = useState(50);

  useEffect(() => {
    if (companyId) {
      loadCalls();
      loadStats();
    }
  }, [companyId]);

  // Load calls when filters change
  useEffect(() => {
    if (companyId) {
      loadCalls();
    }
  }, [filters, currentPage]);

  // Load live calls after stored calls are loaded
  useEffect(() => {
    if (calls.length > 0 || (!loading && calls.length === 0)) {
      loadLiveCalls();
    }
  }, [calls, loading]);

  const loadCalls = async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * callsPerPage;

      const apiParams = {
        limit: callsPerPage,
        offset: offset,
        period: filters.period,
        query: filters.query,
        status: filters.status,
        agent: filters.agent,
        queue: filters.queue,
        start_date: filters.start_date,
        end_date: filters.end_date,
        sort: "desc",
        sort_by: "started_at",
      };

      const response = await getPhoneroStoredCalls(apiParams);

      if (response.data && response.data.success) {
        setCalls(response.data.calls);
        setTotalCalls(response.data.total || 0);
      } else {
        setCalls([]);
        setTotalCalls(0);
      }
    } catch (err) {
      setError("Failed to load calls");
      console.error("Error loading calls:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setStatsLoading(true);

    try {
      const response = await fetchPhoneroStats();

      if (response.data && response.data.success) {
        const statsData = response.data.stats;

        // Transform the stats to match our component's expected format
        const transformedStats = {
          today: {
            totalCalls: statsData.today?.total_calls || 0,
            totalDuration: statsData.today?.total_duration || 0,
            answeredCalls: statsData.today?.answered_calls || 0,
            missedCalls: statsData.today?.missed_calls || 0,
            lastUpdated: statsData.today?.last_updated || null,
            error: statsData.today?.error || null,
          },
          this_week: {
            totalCalls: statsData.this_week?.total_calls || 0,
            totalDuration: statsData.this_week?.total_duration || 0,
            answeredCalls: statsData.this_week?.answered_calls || 0,
            missedCalls: statsData.this_week?.missed_calls || 0,
            lastUpdated: statsData.this_week?.last_updated || null,
            error: statsData.this_week?.error || null,
          },
          this_month: {
            totalCalls: statsData.this_month?.total_calls || 0,
            totalDuration: statsData.this_month?.total_duration || 0,
            answeredCalls: statsData.this_month?.answered_calls || 0,
            missedCalls: statsData.this_month?.missed_calls || 0,
            lastUpdated: statsData.this_month?.last_updated || null,
            error: statsData.this_month?.error || null,
          },
        };

        setStats(transformedStats);
      } else {
        // Handle case where API returns success: false
        console.warn("Stats API returned success: false", response.data);
        setStats({
          today: {
            totalCalls: 0,
            totalDuration: 0,
            answeredCalls: 0,
            missedCalls: 0,
            lastUpdated: null,
            error: "Failed to load stats",
          },
          this_week: {
            totalCalls: 0,
            totalDuration: 0,
            answeredCalls: 0,
            missedCalls: 0,
            lastUpdated: null,
            error: "Failed to load stats",
          },
          this_month: {
            totalCalls: 0,
            totalDuration: 0,
            answeredCalls: 0,
            missedCalls: 0,
            lastUpdated: null,
            error: "Failed to load stats",
          },
        });
      }
    } catch (err) {
      console.error("Error loading stats:", err);
      // Set error state for all periods
      setStats({
        today: {
          totalCalls: 0,
          totalDuration: 0,
          answeredCalls: 0,
          missedCalls: 0,
          lastUpdated: null,
          error: "Network error",
        },
        this_week: {
          totalCalls: 0,
          totalDuration: 0,
          answeredCalls: 0,
          missedCalls: 0,
          lastUpdated: null,
          error: "Network error",
        },
        this_month: {
          totalCalls: 0,
          totalDuration: 0,
          answeredCalls: 0,
          missedCalls: 0,
          lastUpdated: null,
          error: "Network error",
        },
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearch = () => {
    loadCalls();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const loadLiveCalls = async () => {
    setLiveCallsLoading(true);
    try {
      const response = await fetchPhoneroLiveCalls(10);

      if (response.data && response.data.success) {
        // Get the list of call_ids that are already stored in our database
        const storedCallIds = calls.map((call) => call.call_id);

        // Filter out calls that are already synced (exist in our database)
        const unsyncedCalls = response.data.calls.filter((call) => {
          // If the call_id exists in our stored calls, it's already synced
          if (storedCallIds.includes(call.call_id)) {

            return false;
          }
          return true;
        });

        setLiveCalls(unsyncedCalls);
      }
    } catch (err) {
      console.error("Error loading live calls:", err);
    } finally {
      setLiveCallsLoading(false);
    }
  };

  const handleFetchLiveCalls = async () => {
    await loadLiveCalls(); // Reuse the loadLiveCalls function
  };

  const handleOpenNotesDialog = (call) => {
    if (call && call.id) {
      setSelectedCall(call);
      setShowNotesDialog(true);
    } else {
      console.warn(
        "Cannot open notes dialog: call or call.id is missing",
        call
      );
      // You could add a toast notification here to inform the user
    }
  };

  const handleOpenCreateCaseDialog = (call) => {
    if (call && call.call_id) {
      setSelectedCallForCase(call);
      setShowCreateCaseDialog(true);
    } else {
      console.warn(
        "Cannot open create case dialog: call or call.call_id is missing",
        call
      );
    }
  };

  const handleCaseCreated = (caseData) => {
    // Refresh both live calls and stored calls to reflect any changes
    loadLiveCalls();
    loadCalls();
    toast.success(`Case ${caseData.case_number} created successfully!`);
  };

  const handleCallProcessed = () => {
    // Refresh both live calls and stored calls to reflect the status change
    loadLiveCalls();
    loadCalls();
  };

  const handleCallLinked = () => {
    // Refresh both live calls and stored calls to reflect the status change
    loadLiveCalls();
    loadCalls();
  };

  const handleManualCallCreated = (call) => {
    // Refresh the calls list to show the new manual call
    loadCalls();
    loadLiveCalls();
    toast.success("Manual call added successfully");
  };

  const handleProcessWithoutCase = async (call) => {
    const reason = prompt(
      "Enter a reason for processing this call without a case (optional):"
    );

    try {
      const response = await processCallWithoutCase({
        phonero_call_id: call.call_id,
        reason: reason || "Processed without creating case",
      });

      if (response.success) {
        toast.success("Call marked as processed!");
        loadLiveCalls();
        loadCalls();
      } else {
        toast.error(response.message || "Failed to process call");
      }
    } catch (err) {
      console.error("Error processing call:", err);
      toast.error("Failed to process call");
    }
  };

  const refreshStatsForPeriod = async (periodType) => {
    setRefreshingStats((prev) => ({ ...prev, [periodType]: true }));

    try {
      const response = await refreshPhoneroStats(periodType);

      if (response.data && response.data.success) {
        const statData = response.data.stats;

        setStats((prev) => ({
          ...prev,
          [periodType]: {
            totalCalls: statData.total_calls || 0,
            totalDuration: statData.total_duration || 0,
            answeredCalls: statData.answered_calls || 0,
            missedCalls: statData.missed_calls || 0,
            lastUpdated: statData.last_updated || null,
            error: null,
          },
        }));
      } else {
        // Handle refresh failure
        setStats((prev) => ({
          ...prev,
          [periodType]: {
            ...prev[periodType],
            error: "Failed to refresh stats",
          },
        }));
      }
    } catch (err) {
      console.error(`Error refreshing stats for ${periodType}:`, err);
      setStats((prev) => ({
        ...prev,
        [periodType]: {
          ...prev[periodType],
          error: "Network error",
        },
      }));
    } finally {
      setRefreshingStats((prev) => ({ ...prev, [periodType]: false }));
    }
  };

  const formatTotalDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds < 0) return "0s";

    // Handle extremely large numbers (likely errors)
    if (seconds > 31536000) {
      // More than 1 year
      console.warn("Suspicious total duration value:", seconds);
      return "0s";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const getPeriodDisplayName = (periodType) => {
    switch (periodType) {
      case "today":
        return "Today";
      case "this_week":
        return "This Week";
      case "this_month":
        return "This Month";
      default:
        return periodType;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadCalls}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <RoleOnly role="phone" action="read">
      <div className="space-y-6">
        {/* Statistics */}
        {statsLoading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white overflow-hidden shadow rounded-lg animate-pulse"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-6 w-6 bg-gray-300 rounded"></div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                      <div className="h-6 bg-gray-300 rounded w-1/2 mb-1"></div>
                      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {Object.entries(stats).map(([periodType, periodStats]) => (
              <div
                key={periodType}
                className="bg-white overflow-hidden shadow rounded-lg"
              >
                <div className="p-5 relative">
                  <div className="flex flex-col h-full justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {periodType === "today" && (
                          <ChartBarIcon className="h-5 w-5 text-blue-600" />
                        )}
                        {periodType === "this_week" && (
                          <ClockIcon className="h-5 w-5 text-green-600" />
                        )}
                        {periodType === "this_month" && (
                          <UserIcon className="h-5 w-5 text-purple-600" />
                        )}
                        <span className="text-sm font-medium text-gray-500 truncate">
                          {getPeriodDisplayName(periodType)}
                        </span>
                      </div>
                      <div className="mt-2">
                        <span className="text-2xl font-bold text-gray-900">
                          {periodStats.error ? (
                            <span className="text-red-600">{t("Error")}</span>
                          ) : (
                            periodStats.totalCalls || 0
                          )}
                        </span>
                        <span className="text-base font-medium text-gray-700 ml-1">
                          {t("calls")}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {periodStats.error
                          ? periodStats.error
                          : formatTotalDuration(periodStats.totalDuration || 0)}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>
                          {periodStats.answeredCalls || 0} {t("answered")}
                        </span>
                        <span className="text-gray-300">|</span>
                        <span>
                          {periodStats.missedCalls || 0} {t("missed")}
                        </span>
                      </div>
                    </div>
                    {periodStats.lastUpdated && (
                      <div className="text-xs text-gray-400 mt-3">
                        Updated:{" "}
                        {new Date(periodStats.lastUpdated).toLocaleString(
                          "nb-NO"
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => refreshStatsForPeriod(periodType)}
                    disabled={refreshingStats[periodType]}
                    className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 bg-white rounded-full"
                    title="Refresh stats"
                    style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}
                  >
                    <ArrowPathIcon
                      className={`h-4 w-4 ${
                        refreshingStats[periodType] ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live Calls Section */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t("Live Calls (Not synched yet)")}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {t(
                    "Latest calls from Phonero API (not yet synced to database)"
                  )}
                </p>
              </div>
              <button
                onClick={handleFetchLiveCalls}
                disabled={liveCallsLoading}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {liveCallsLoading ? (
                  <>
                    <ArrowPathIcon className="animate-spin -ml-0.5 mr-2 h-4 w-4" />
                    {t("Refreshing...")}
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    {t("Refresh")}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="px-4 py-4">
            {liveCallsLoading ? (
              <div className="text-center py-4">
                <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                <p className="mt-2 text-sm text-gray-500">
                  {t("Loading live calls...")}
                </p>
              </div>
            ) : liveCalls.length === 0 ? (
              <div className="text-center py-4">
                <PhoneIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">
                  {t("No unsynced live calls found")}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {liveCalls.map((call, index) => (
                  <CallItem
                    key={index}
                    call={call}
                    index={index}
                    showHover={false}
                    onOpenNotes={handleOpenNotesDialog}
                    onOpenCreateCase={handleOpenCreateCaseDialog}
                    onProcessWithoutCase={handleProcessWithoutCase}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Search and Filter Section */}
        <SearchAndFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
        />

        {/* Calls List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Calls
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Latest call activity from Phonero (showing {callsPerPage}{" "}
                  calls per page)
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddManualCall(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  {t("Add Manual Call")}
                </button>
                {loading && (
                  <div className="flex items-center text-sm text-gray-500">
                    <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                    Loading...
                  </div>
                )}
              </div>
            </div>
          </div>

          {calls.length === 0 && !loading ? (
            <div className="text-center py-8">
              <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No calls found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                No call data available for the selected filters.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-200">
                {calls.map((call, index) => (
                  <CallItem
                    key={index}
                    call={call}
                    index={index}
                    showHover={true}
                    onOpenNotes={handleOpenNotesDialog}
                    onOpenCreateCase={handleOpenCreateCaseDialog}
                    onProcessWithoutCase={handleProcessWithoutCase}
                  />
                ))}
              </ul>

              {/* Pagination */}
              {totalCalls > callsPerPage && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalCalls}
                  itemsPerPage={callsPerPage}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Notes Dialog */}
      {showNotesDialog && selectedCall && selectedCall.id && (
        <PMDialog
          open={showNotesDialog}
          onClose={() => setShowNotesDialog(false)}
          title={`Notes - Call ${selectedCall.from_number || "Unknown"} → ${
            selectedCall.to_number || "Unknown"
          }`}
          size="4xl"
        >
          {selectedCall && selectedCall.id ? (
            <div>
              {console.log("Rendering NotesAdvancedSection with:", {
                module: "phone",
                module_param: selectedCall.id,
              })}
              <NotesAdvancedSection
                module="phone"
                module_param={selectedCall.id}
                title="Call Notes"
                subtitle={`Notes for call from ${
                  selectedCall.from_number || "Unknown"
                } to ${selectedCall.to_number || "Unknown"}`}
                enableFileUpload={true}
                enableTemplates={true}
                enableTags={true}
              />
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              Loading call data...
            </div>
          )}
        </PMDialog>
      )}

      {/* Create Case Dialog */}
      {showCreateCaseDialog && selectedCallForCase && (
        <CreateCaseDialog
          open={showCreateCaseDialog}
          onClose={() => setShowCreateCaseDialog(false)}
          call={selectedCallForCase}
          onCaseCreated={handleCaseCreated}
          onCallProcessed={handleCallProcessed}
        />
      )}

      {/* Add Manual Call Dialog */}
      {showAddManualCall && (
        <AddManualCall
          onCallCreated={handleManualCallCreated}
          onClose={() => setShowAddManualCall(false)}
        />
      )}
    </RoleOnly>
  );
};

export default CallsList;
